import { useEffect, useRef, useState, type MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { categories, mainSiteUrl } from "@/mocks/categories";

const languageOptions = [
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "fr", label: "Français", flag: "🇫🇷" },
  { code: "da", label: "Dansk", flag: "🇩🇰" },
  { code: "de", label: "Deutsch", flag: "🇩🇪" },
];

type NavLink =
  | { key: string; type: "link"; to: string; label: string }
  | { key: string; type: "anchor"; id: string; label: string };

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCategoriesOpen, setMobileCategoriesOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const changeLanguage = (code: string) => {
    i18n.changeLanguage(code);
    setLangOpen(false);
    setMobileOpen(false);
  };

  const goToSection = (id: string) => (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setMobileOpen(false);
    if (isHome) {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate("/");
      window.setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 250);
    }
  };

  const navLinks: NavLink[] = [
    { key: "home", type: "link", to: "/", label: t("nav.home") },
    { key: "spreadsheet", type: "anchor", id: "spreadsheet", label: t("nav.spreadsheet") },
    { key: "categories", type: "anchor", id: "categories", label: t("nav.categories") },
    { key: "shoppingGuide", type: "link", to: "/allchinabuy-spreadsheet-guide", label: t("nav.shoppingGuide") },
    { key: "faq", type: "anchor", id: "faq", label: t("nav.faq") },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-background-50 ${
        scrolled ? "shadow-sm border-b border-background-200" : "border-b border-background-200"
      }`}
    >
      <div className="px-4 md:px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between h-16">
          {/* Brand */}
          <Link to="/" className="flex items-center gap-2.5 shrink-0">
            <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-table-line text-base"></i>
            </span>
            <span className="font-heading font-bold text-foreground-950 text-base leading-tight whitespace-nowrap">
              {t("brandShort")}
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-6">
            {navLinks.map((link) =>
              link.type === "link" ? (
                <Link
                  key={link.key}
                  to={link.to}
                  className="text-sm font-semibold text-foreground-800 hover:text-primary-600 whitespace-nowrap transition-colors"
                >
                  {link.label}
                </Link>
              ) : (
                <a
                  key={link.key}
                  href={`/#${link.id}`}
                  onClick={goToSection(link.id)}
                  className="text-sm font-semibold text-foreground-800 hover:text-primary-600 whitespace-nowrap transition-colors"
                >
                  {link.label}
                </a>
              )
            )}
          </nav>

          <div className="flex items-center gap-2.5">
            {/* Language switcher */}
            <div className="relative" ref={langRef}>
              <button
                onClick={() => setLangOpen((v) => !v)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-800 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                aria-label="Language"
              >
                <i className="ri-global-line text-sm"></i>
                <span className="hidden sm:inline">
                  {languageOptions.find((l) => l.code === i18n.language)?.label || "English"}
                </span>
                <i className="ri-arrow-down-s-line text-sm"></i>
              </button>
              {langOpen && (
                <div className="absolute right-0 mt-2 w-40 rounded-lg border border-background-200 bg-background-50 py-1.5 shadow-lg">
                  {languageOptions.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`w-full flex items-center gap-2.5 px-4 py-2 text-sm text-left hover:bg-background-100 transition-colors cursor-pointer ${
                        i18n.language === lang.code ? "text-primary-600 font-semibold" : "text-foreground-800"
                      }`}
                    >
                      <span>{lang.flag}</span>
                      <span className="whitespace-nowrap">{lang.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* CTA */}
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="hidden sm:inline-flex items-center px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              {t("hero.ctaSecondary")}
            </a>

            {/* Mobile toggle */}
            <button
              onClick={() => setMobileOpen((v) => !v)}
              className="md:hidden w-10 h-10 flex items-center justify-center rounded-md text-foreground-950 cursor-pointer"
              aria-label="Menu"
            >
              <i className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 px-4 py-4 max-h-[75vh] overflow-y-auto">
          <nav className="flex flex-col gap-1">
            {navLinks.map((link) =>
              link.type === "link" ? (
                <Link
                  key={link.key}
                  to={link.to}
                  onClick={() => setMobileOpen(false)}
                  className="px-3 py-2.5 rounded-md text-sm font-semibold text-foreground-900 hover:bg-background-100 transition-colors"
                >
                  {link.label}
                </Link>
              ) : (
                <a
                  key={link.key}
                  href={`/#${link.id}`}
                  onClick={goToSection(link.id)}
                  className="px-3 py-2.5 rounded-md text-sm font-semibold text-foreground-900 hover:bg-background-100 transition-colors"
                >
                  {link.label}
                </a>
              )
            )}

            {/* Mobile categories accordion */}
            <button
              onClick={() => setMobileCategoriesOpen((v) => !v)}
              className="flex items-center justify-between px-3 py-2.5 rounded-md text-sm font-semibold text-foreground-900 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <span className="flex items-center gap-2">
                <i className="ri-grid-line text-foreground-500"></i>
                {t("nav.browseCategories")}
              </span>
              <i className={`ri-arrow-down-s-line text-foreground-500 transition-transform ${mobileCategoriesOpen ? "rotate-180" : ""}`}></i>
            </button>

            {mobileCategoriesOpen && (
              <div className="grid grid-cols-2 gap-1 pl-3 pr-1 pb-1">
                {categories.map((cat) => (
                  <a
                    key={cat.id}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-2 px-2 py-2 rounded-md text-sm text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap"
                  >
                    <span className="w-6 h-6 shrink-0 rounded overflow-hidden">
                      <img src={cat.image} alt={cat.imageAlt} className="w-full h-full object-cover object-top" />
                    </span>
                    <span className="truncate">{t(`categories.${cat.id}`)}</span>
                  </a>
                ))}
              </div>
            )}

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="mt-2 px-3 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold text-center"
            >
              {t("hero.ctaSecondary")}
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}